import { useGetModulesQuery } from 'store/services/core/modules';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ModalsType } from 'types';
import { ActionModal } from 'components/mui/common/ActionModal';
import { Table } from 'components/common/Table/Table';
import { SearchField } from 'components/common/Table/actions';
import { modulesDataScheme } from 'utils/table/admin/core/modules';
import { getModuleAssistantsSummary } from 'utils/table/admin/core/modulesFunctions';
import { transformDataByDataSchema } from 'utils/common';
import { useTableQueryControls } from 'utils/hooks';

interface Props extends ModalsType {
  defaultSelectedId?: string;
  defaultSelectedName?: string;
  onConfirm: (module: { id: string; name: string }) => void;
}

const MODULE_PICKER_HEIGHT = 'min(430px, 65vh)';
const MODULE_PICKER_COLUMN_SIZE = 130;

export const SelectTemplateModuleModal = memo(({ open, onClose, defaultSelectedId, defaultSelectedName, onConfirm }: Props) => {
  const { t, i18n } = useTranslation();
  const [selectedModuleId, setSelectedModuleId] = useState(defaultSelectedId ?? '');
  const [selectedModuleName, setSelectedModuleName] = useState(defaultSelectedName ?? '');
  const previousOpenRef = useRef<boolean>();
  const latestDefaultsRef = useRef({ id: defaultSelectedId ?? '', name: defaultSelectedName ?? '' });
  const { state, setPagination, setSearch, setSort } = useTableQueryControls<typeof modulesDataScheme, Record<string, never>>(
    'heliosTemplateModuleModal',
    {
      filters: {},
      sort: { by: 'name', order: 'asc' },
      search: '',
      pagination: { page: 1, pageSize: 5 },
    }
  );

  useEffect(() => {
    latestDefaultsRef.current = { id: defaultSelectedId ?? '', name: defaultSelectedName ?? '' };

    if (open && (!previousOpenRef.current || !selectedModuleId)) {
      setSelectedModuleId(latestDefaultsRef.current.id);
      setSelectedModuleName(latestDefaultsRef.current.name);
    } else if (open && defaultSelectedId === selectedModuleId && defaultSelectedName && !selectedModuleName) {
      setSelectedModuleName(defaultSelectedName);
    }

    previousOpenRef.current = open;
  }, [defaultSelectedId, defaultSelectedName, open, selectedModuleId, selectedModuleName]);

  const { data, isFetching } = useGetModulesQuery(
    {
      page: state?.pagination.page ?? 1,
      pageSize: state?.pagination.pageSize ?? 5,
      isFlowVisible: undefined,
      isHeliosVisible: true,
      search: state?.search ?? '',
      orderBy: state?.sort?.by ?? 'name',
      orderDesc: state?.sort?.order === 'desc',
    },
    { skip: !open }
  );

  const moduleRows = useMemo(() => {
    const items = (data?.items ?? []).map((module) => {
      const assistantsSummary = getModuleAssistantsSummary(module.assistants, i18n.language);

      return {
        ...module,
        assistants:
          assistantsSummary.kind === 'multiple'
            ? `${assistantsSummary.count} ${t('table.header.assistants')}`
            : assistantsSummary.kind === 'single'
              ? assistantsSummary.label
              : t('common.noneMale'),
      };
    });

    return transformDataByDataSchema(items, modulesDataScheme);
  }, [data?.items, i18n.language, t]);

  const handleConfirm = useCallback(() => {
    if (!selectedModuleId) return;

    onConfirm({ id: selectedModuleId, name: selectedModuleName });
  }, [onConfirm, selectedModuleId, selectedModuleName]);

  const handleRowSelect = useCallback(
    (rowIds: string[]) => {
      const nextSelectedModuleId = rowIds[0] ?? '';
      const nextSelectedModule = data?.items.find((module) => module.id === nextSelectedModuleId);

      setSelectedModuleId(nextSelectedModuleId);
      setSelectedModuleName(nextSelectedModule?.name ?? '');
    },
    [data?.items]
  );

  const handleSort = useCallback(
    ({ column, direction }: { column: keyof typeof modulesDataScheme; direction: 'asc' | 'desc' }) => setSort({ by: column, order: direction }),
    [setSort]
  );

  const handlePageSizeChange = useCallback((pageSize: number) => setPagination({ page: 1, pageSize }), [setPagination]);

  const handleCurrentPageChange = useCallback(
    (page: number) => setPagination({ page, pageSize: state?.pagination.pageSize ?? 5 }),
    [setPagination, state?.pagination.pageSize]
  );

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      onCancel={onClose}
      onSubmit={handleConfirm}
      title={t('helios.templates.settings.selectModuleModal.title')}
      submitText={t('common.select')}
      isOnSubmitDisabled={!selectedModuleId}
      maxWidth="xl">
      <Table
        isLoading={isFetching}
        dataSchema={modulesDataScheme}
        data={moduleRows}
        rows={data?.totalCount ?? 0}
        page={data?.page ?? 1}
        pageSize={data?.pageSize ?? 5}
        sort={{
          column: state?.sort?.by ?? 'name',
          direction: state?.sort?.order ?? 'asc',
        }}
        controls={
          <SearchField
            placeholder={t('common.search.modules')}
            value={state?.search ?? ''}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ mt: 2 }}
            dataTestId="admin-helios-template-select-module-search"
          />
        }
        isMultiSelect={false}
        defaultSelectedRows={selectedModuleId ? [selectedModuleId] : []}
        onSelectRow={handleRowSelect}
        onSort={handleSort}
        onPageSizeChange={handlePageSizeChange}
        onCurrentPageChange={handleCurrentPageChange}
        fixedHeight={MODULE_PICKER_HEIGHT}
        defaultColumnSize={MODULE_PICKER_COLUMN_SIZE}
        isModal
        dataTestIdPrefix="admin-helios-template-select-module"
      />
    </ActionModal>
  );
});
